import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { IonicModule, ModalController } from '@ionic/angular';
import { addIcons } from 'ionicons';
import { arrowForward, barbellOutline, close, giftOutline, shareSocialOutline } from 'ionicons/icons';
import { AppButtonComponent } from '../app-button/app-button.component';
import { getImageUrl, resolveApiMediaById } from '../../utils/media-url.util';

addIcons({ arrowForward, barbellOutline, close, giftOutline, shareSocialOutline });

/**
 * Tela cheia com a imagem do brinde para chamar atenção para a campanha de indicação.
 * Mostra o brinde, os passos até ganhar e mantém "Quero participar" sempre visível.
 * "Quero participar" fecha e abre Indique e ganhe.
 */
@Component({
  selector: 'app-referral-highlight-modal',
  standalone: true,
  imports: [IonicModule, CommonModule, AppButtonComponent],
  template: `
    <ion-content class="highlight-content">
      <button slot="fixed" type="button" class="close" aria-label="Fechar" (click)="close('dismiss')">
        <ion-icon name="close"></ion-icon>
      </button>
      <div class="highlight">
        <div class="hero">
          @if (imageUrl) {
            <img [src]="imageUrl" [alt]="prizeName" class="prize" />
          } @else {
            <div class="prize prize--placeholder"><ion-icon name="gift-outline"></ion-icon></div>
          }
        </div>
        <div class="body">
          <span class="tag">Indique e ganhe</span>
          <h1>Ganhe {{ prizeName }}</h1>
          @if (prizeDescription) {
            <p class="summary">{{ prizeDescription }}</p>
          } @else {
            <p class="summary">Indique {{ goalLabel }} e ganhe o brinde em casa.</p>
          }
          <section class="how" aria-labelledby="referral-how-title">
            <h2 id="referral-how-title">Como funciona</h2>
            <ol class="steps">
              <li class="step">
                <span class="step__icon"><ion-icon name="share-social-outline"></ion-icon></span>
                <div>
                  <strong>Compartilhe seu código</strong>
                  <span>Participe e envie seu código para os amigos.</span>
                </div>
              </li>
              <li class="step">
                <span class="step__icon"><ion-icon name="barbell-outline"></ion-icon></span>
                <div>
                  <strong>Seus amigos começam</strong>
                  <span>Aluno vale ao concluir o 1º treino; personal, ao ter o 1º aluno.</span>
                </div>
              </li>
              <li class="step">
                <span class="step__icon"><ion-icon name="gift-outline"></ion-icon></span>
                <div>
                  <strong>Receba em casa</strong>
                  <span>Com {{ goalLabel }} válidos, o brinde é seu.</span>
                </div>
              </li>
            </ol>
          </section>
        </div>
      </div>
    </ion-content>
    <ion-footer class="ion-no-border">
      <div class="actions">
        <app-button
          label="Quero participar"
          icon="arrow-forward"
          iconPosition="end"
          fill="solid"
          expand="block"
          size="large"
          shape="round"
          (buttonClick)="close('join')"
        ></app-button>
        <button type="button" class="later" (click)="close('dismiss')">Agora não</button>
      </div>
    </ion-footer>
  `,
  styles: [
    `
      .highlight-content {
        --background: var(--ion-background-color);
      }
      .close {
        position: absolute;
        top: calc(env(safe-area-inset-top) + 12px);
        right: 12px;
        width: 40px;
        height: 40px;
        display: grid;
        place-items: center;
        border: none;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.55);
        font-size: 24px;
        color: #fff;
      }
      .highlight {
        max-width: 480px;
        margin: 0 auto;
      }
      .hero {
        position: relative;
      }
      .hero::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(to bottom, transparent 60%, var(--ion-background-color));
        pointer-events: none;
      }
      .prize {
        display: block;
        width: 100%;
        max-height: min(40vh, 300px);
        object-fit: cover;
      }
      .prize--placeholder {
        height: 200px;
        display: grid;
        place-items: center;
        font-size: 96px;
        color: var(--ion-color-primary);
        background: rgba(var(--ion-color-primary-rgb), 0.08);
      }
      .body {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 0 24px 16px;
      }
      .tag {
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        padding: 4px 12px;
        border-radius: 12px;
        background: rgba(var(--ion-color-primary-rgb), 0.15);
        color: var(--ion-color-primary);
      }
      h1 {
        margin: 12px 0 0;
        font-family: var(--app-font-headline);
        font-size: 1.7rem;
        font-weight: 800;
        line-height: 1.15;
        color: var(--ion-text-color);
      }
      .summary {
        margin: 8px 0 0;
        line-height: 1.45;
        color: var(--ion-color-step-700);
      }
      .how {
        width: 100%;
        margin-top: 20px;
        padding: 16px;
        border: 1px solid var(--ion-color-step-200);
        border-radius: 16px;
        text-align: left;
      }
      h2 {
        margin: 0 0 14px;
        font-size: 0.95rem;
        font-weight: 700;
        color: var(--ion-text-color);
      }
      .steps {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      .step {
        position: relative;
        display: flex;
        gap: 12px;
        padding-bottom: 14px;
      }
      .step:last-child {
        padding-bottom: 0;
      }
      .step:not(:last-child)::before {
        content: '';
        position: absolute;
        top: 36px;
        bottom: 0;
        left: 17px;
        width: 2px;
        background: var(--ion-color-step-200);
      }
      .step__icon {
        flex: 0 0 36px;
        width: 36px;
        height: 36px;
        display: grid;
        place-items: center;
        border-radius: 50%;
        font-size: 18px;
        background: rgba(var(--ion-color-primary-rgb), 0.15);
        color: var(--ion-color-primary);
      }
      .step strong {
        display: block;
        font-size: 0.95rem;
        color: var(--ion-text-color);
      }
      .step span:not(.step__icon) {
        display: block;
        margin-top: 2px;
        font-size: 0.85rem;
        line-height: 1.35;
        color: var(--ion-color-step-700);
      }
      ion-footer {
        background: var(--ion-background-color);
      }
      .actions {
        max-width: 480px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 8px 24px calc(env(safe-area-inset-bottom) + 8px);
      }
      .later {
        background: none;
        border: none;
        padding: 10px;
        color: var(--ion-color-step-600);
        font-size: 0.95rem;
      }
    `
  ]
})
export class ReferralHighlightModalComponent {
  @Input() prizeName = '';
  @Input() prizeDescription = '';
  @Input() prizeImageUrl = '';
  @Input() goal = 1;

  constructor(
    private modalController: ModalController,
    private router: Router
  ) {}

  get imageUrl(): string {
    return resolveApiMediaById(this.prizeImageUrl) || getImageUrl(this.prizeImageUrl);
  }

  get goalLabel(): string {
    return `${this.goal} ${this.goal === 1 ? 'amigo' : 'amigos'}`;
  }

  async close(role: 'join' | 'dismiss'): Promise<void> {
    await this.modalController.dismiss(null, role);
    if (role === 'join') {
      void this.router.navigate(['/referral']);
    }
  }
}
